// Uploaded files (logos, dashboard images). The API serves them behind the access token, so
// an <img> cannot point at the URL directly: the bytes are fetched once and shown through a
// blob URL.
import type { FileInfo, UploadFileRequest } from '@twinlink/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, apiFetch } from './api';

export function useFileUrl(orgId: string | undefined, fileId: string | null | undefined): string | null {
  const blob = useQuery({
    queryKey: ['org', orgId, 'file', fileId],
    queryFn: async () => (await apiFetch(`/orgs/${orgId}/files/${fileId}`)).blob(),
    enabled: !!orgId && !!fileId,
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    retry: false,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob.data) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(blob.data);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob.data]);
  return fileId ? url : null;
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read'));
    // "data:image/png;base64,AAAA" → "AAAA"
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.readAsDataURL(file);
  });
}

export function useUploadFile(orgId: string) {
  return useMutation({
    mutationFn: async ({ file, kind }: { file: File; kind: UploadFileRequest['kind'] }) =>
      api<FileInfo>(`/orgs/${orgId}/files`, { method: 'POST', body: { kind, name: file.name, dataBase64: await toBase64(file) } satisfies UploadFileRequest }),
  });
}
